// src/pages/MedicationsPage.js
import { useState, useEffect } from 'react';
import axios from 'axios';
import { User, Calendar, Clock, XCircle, Plus } from 'lucide-react';

import Header from '../components/Header';
import Footer from '../components/Footer';
import MedicationManager from '../components/MedicationManager';
import { getToken } from '../utils/auth';
import {
  addCustomMedication,
  getAllMedications,
  loadMedicationCatalog,
} from '../data/medications';
import Modal, { DialogHeader, DialogFooter, DialogBody } from '../components/ui/Modal';

const EMPTY_CATALOG_FORM = {
  drugName: '',
  category: '',
  subCategory: '',
  drugClass: '',
  route: '',
  commonDoseStrengths: '',
  commonFrequency: '',
  rxOtc: '',
};

const MedicationsPage = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // "Add Medication to System" (catalog) modal state
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
  const [catalogForm, setCatalogForm] = useState(EMPTY_CATALOG_FORM);
  const [catalogError, setCatalogError] = useState(null);
  const [catalogSuccess, setCatalogSuccess] = useState(null);
  const [isSavingCatalogEntry, setIsSavingCatalogEntry] = useState(false);
  // Bumped whenever the catalog changes so children can refresh derived data.
  const [catalogVersion, setCatalogVersion] = useState(0);

  // Warm the medication catalog cache on page mount so the autocomplete picks
  // up the DB list (not just the embedded fallback) as early as possible.
  useEffect(() => {
    let cancelled = false;
    loadMedicationCatalog().then(() => {
      if (!cancelled) setCatalogVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleOpenCatalogModal = () => {
    setCatalogForm(EMPTY_CATALOG_FORM);
    setCatalogError(null);
    setIsCatalogModalOpen(true);
  };

  const handleCloseCatalogModal = () => {
    if (isSavingCatalogEntry) return;
    setIsCatalogModalOpen(false);
  };

  useEffect(() => {
    if (!isCatalogModalOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === 'Escape' && !isSavingCatalogEntry) setIsCatalogModalOpen(false);
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [isCatalogModalOpen, isSavingCatalogEntry]);

  const handleSaveCatalogMedication = async () => {
    const name = catalogForm.drugName.trim();
    if (!name) {
      setCatalogError('Drug Name is required.');
      return;
    }
    // Quick local duplicate check against the current cache; the server also
    // enforces a case-insensitive unique constraint, so this is just a UX
    // shortcut to avoid a round-trip for obvious duplicates.
    const existing = getAllMedications();
    if (existing.some((m) => m.drugName.toLowerCase() === name.toLowerCase())) {
      setCatalogError(`"${name}" already exists in the catalog.`);
      return;
    }

    try {
      setIsSavingCatalogEntry(true);
      setCatalogError(null);
      await addCustomMedication(catalogForm);
      setCatalogVersion((v) => v + 1);
      setIsCatalogModalOpen(false);
      setCatalogSuccess(`"${name}" added to the medication catalog.`);
      setTimeout(() => setCatalogSuccess(null), 3500);
    } catch (err) {
      setCatalogError(err.message || 'Failed to add medication.');
    } finally {
      setIsSavingCatalogEntry(false);
    }
  };

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const token = getToken();
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${API_BASE_URL}/patients/`, { headers });
        // console.log("API PATIENT RESPONSE:", response.data);
	setPatients(response.data);
        setFilteredPatients(response.data);
      } catch (error) {
        console.error('Error fetching patients:', error);
      }
    };
    fetchPatients();
  }, [API_BASE_URL]);

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
  };

  useEffect(() => {
    if (!searchTerm) {
      setFilteredPatients(patients);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();
    const filtered = patients.filter((patient) => {
      const patientName = `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.toLowerCase();
      const patientId = String(patient.MRN ?? patient.id ?? '').toLowerCase();
      const diagnosis = (patient.diagnosis ?? '').toLowerCase();
      const gender = (patient.gender ?? '').toLowerCase();

      return (
        patientName.includes(lowerSearch) ||
        patientId.includes(lowerSearch) ||
        diagnosis.includes(lowerSearch) ||
        gender.includes(lowerSearch)
      );
    });

    setFilteredPatients(filtered);
  }, [searchTerm, patients]);

  const PatientTable = ({ patients, onSelectPatient }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="p-6 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-800">All Patients</h3>
        <input
          type="text"
          placeholder="Search by Name, ID, Diagnosis, Gender"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          style={{ minWidth: '250px' }}
        />
      </div>
      <div className="overflow-x-auto max-h-96">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">MRN</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Gender</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Diagnosis</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {patients.map((patient) => (
              <tr key={patient.id ?? patient.MRN} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-700">{patient.MRN ?? patient.id}</td>
                <td className="px-6 py-4 text-sm font-medium text-teal-600">
                  {patient.firstName} {patient.lastName}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">{patient.gender}</td>
                <td className="px-6 py-4 text-sm text-gray-700">{patient.diagnosis}</td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => onSelectPatient(patient)}
                    className="text-teal-600 hover:text-teal-800 text-sm font-medium"
                  >
                    View Medications
                  </button>
                </td>
              </tr>
            ))}
            {patients.length === 0 && (
              <tr>
                <td colSpan="100%" className="h-[300px]">
                  <div className="flex flex-col items-center justify-center h-full gap-3">
                    <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
                    <p className="text-gray-600 font-medium">Loading patients...</p>
                  </div>
                </td>
                {/* <td colSpan="5" className="text-center py-4 text-gray-500">
                  No patients found
                </td> */}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const PatientProfileCard = ({ patient }) => {
    if (!patient) return null;

    return (
      <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border border-teal-200 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">
                {patient.firstName} {patient.lastName}
              </h3>
              <p className="text-gray-600 text-sm">MRN: #{patient.MRN ?? patient.id}</p>
              <div className="flex items-center space-x-4 mt-3">
                <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
                  <Calendar className="w-4 h-4 text-orange-500" />
                  <span className="text-sm text-gray-700">{patient.nextVisit || 'N/A'}</span>
                </div>
                <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-gray-700">{patient.lastCheckin || 'N/A'}</span>
                </div>
                <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
                  <User className="w-4 h-4 text-teal-500" />
                  <span className="text-sm text-gray-700">{patient.gender ?? 'N/A'}</span>
                </div>
              </div>
              <p className="mt-3 text-gray-600">{patient.diagnosis ?? 'Diagnosis not available'}</p>
            </div>
          </div>
          <div>
            <button
              onClick={() => setSelectedPatient(null)}
              className="text-gray-400 hover:text-red-600"
              aria-label="Close patient profile"
            >
              <XCircle className="w-8 h-8" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="vitra-atmosphere min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto p-4">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Medications Management</h1>
          <button
            onClick={handleOpenCatalogModal}
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-500 text-white text-sm font-medium px-4 py-2 rounded-md shadow-sm transition"
          >
            <Plus className="w-4 h-4" />
            Add Medication to System
          </button>
        </div>

        {catalogSuccess && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg px-4 py-3">
            {catalogSuccess}
          </div>
        )}

        {!selectedPatient && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center space-x-3">
            <div className="bg-blue-500 rounded-full p-2">
              <User className="w-5 h-5 text-white" />
            </div>
            <p className="text-blue-800 text-sm">
              Please select a patient from the table below to view and manage their medications.
            </p>
          </div>
        )}

        <PatientTable patients={filteredPatients} onSelectPatient={handlePatientSelect} />

        {selectedPatient && (
          <>
            <PatientProfileCard patient={selectedPatient} />

            {/* Use the existing MedicationManager component */}
            <MedicationManager
              patientMRN={selectedPatient.MRN ?? selectedPatient.id}
              catalogVersion={catalogVersion}
            />
          </>
        )}
      </main>

      {/* Add Medication to System (catalog) modal */}
      <Modal
        open={isCatalogModalOpen}
        onClose={handleCloseCatalogModal}
        align="start"
      >
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[calc(100vh-6rem)] sm:max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col border border-gray-100">
            <DialogHeader
              title="Add Medication to System"
              subtitle="Adds a new entry to the medication catalog used by the autocomplete."
              onClose={handleCloseCatalogModal}
              tone="muted"
              className={isSavingCatalogEntry ? '[&_button]:opacity-50 [&_button]:pointer-events-none' : ''}
            />

            <DialogBody className="px-6 py-6 space-y-5">
              {catalogError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">
                  {catalogError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Drug Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={catalogForm.drugName}
                  onChange={(e) => setCatalogForm({ ...catalogForm, drugName: e.target.value })}
                  placeholder="e.g., Atorvastatin"
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={catalogForm.category}
                    onChange={(e) => setCatalogForm({ ...catalogForm, category: e.target.value })}
                    placeholder="e.g., Cardiovascular"
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sub-category</label>
                  <input
                    type="text"
                    value={catalogForm.subCategory}
                    onChange={(e) => setCatalogForm({ ...catalogForm, subCategory: e.target.value })}
                    placeholder="e.g., Hypertension"
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Drug Class</label>
                  <input
                    type="text"
                    value={catalogForm.drugClass}
                    onChange={(e) => setCatalogForm({ ...catalogForm, drugClass: e.target.value })}
                    placeholder="e.g., ACE Inhibitor"
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Usual Route</label>
                  <input
                    type="text"
                    value={catalogForm.route}
                    onChange={(e) => setCatalogForm({ ...catalogForm, route: e.target.value })}
                    placeholder="e.g., Oral"
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Common Dose Strengths
                </label>
                <input
                  type="text"
                  value={catalogForm.commonDoseStrengths}
                  onChange={(e) => setCatalogForm({ ...catalogForm, commonDoseStrengths: e.target.value })}
                  placeholder="Separate with semicolons, e.g., 5 mg; 10 mg; 20 mg"
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  These will appear as Dosage suggestions in the patient form.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Common Frequency
                </label>
                <input
                  type="text"
                  value={catalogForm.commonFrequency}
                  onChange={(e) => setCatalogForm({ ...catalogForm, commonFrequency: e.target.value })}
                  placeholder="e.g., Once daily; Twice daily"
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rx / OTC</label>
                <select
                  value={catalogForm.rxOtc}
                  onChange={(e) => setCatalogForm({ ...catalogForm, rxOtc: e.target.value })}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                >
                  <option value="">—</option>
                  <option value="Rx">Rx</option>
                  <option value="OTC">OTC</option>
                  <option value="OTC/Rx">OTC/Rx</option>
                </select>
              </div>
            </DialogBody>

            <DialogFooter>
              <button
                type="button"
                onClick={handleCloseCatalogModal}
                disabled={isSavingCatalogEntry}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 text-sm hover:bg-white transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveCatalogMedication}
                disabled={isSavingCatalogEntry}
                className="px-4 py-2 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-500 transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSavingCatalogEntry ? 'Saving…' : 'Save to Catalog'}
              </button>
            </DialogFooter>
          </div>
      </Modal>

      <Footer />
    </div>
  );
};

export default MedicationsPage;

// // // src/pages/MedicationsPage.js
// import axios from 'axios';
// import { useState, useEffect } from 'react';
// import { User, Calendar, Clock, XCircle, Plus, Edit2, Trash2, Save, X } from 'lucide-react';

// import Header from '../components/Header';
// import Footer from '../components/Footer';

// const MedicationsPage = () => {
//   const API_BASE_URL = process.env.REACT_APP_API_URL;

//   const [patients, setPatients] = useState([]);
//   const [filteredPatients, setFilteredPatients] = useState([]);
//   const [selectedPatient, setSelectedPatient] = useState(null);
//   const [currentMedications, setCurrentMedications] = useState([]);
//   const [medicationHistory, setMedicationHistory] = useState([]);
//   const [loadingMeds, setLoadingMeds] = useState(false);
//   const [errorMeds, setErrorMeds] = useState(null);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [showAddMedForm, setShowAddMedForm] = useState(false);
//   const [editingMed, setEditingMed] = useState(null);
//   const [newMedication, setNewMedication] = useState({
//     PMRN: '',
//     drugName: '',
//     brandName: '',
//     strength: '',
//     form: '',
//     dosage: '',
//     frequency: '',
//     route: '',
//     startDate: '',
//     endDate: '',
//     prn: false,
//     indication: '',
//   });
//   const [addingMedLoading, setAddingMedLoading] = useState(false);
//   const [addingMedError, setAddingMedError] = useState(null);

//   useEffect(() => {
//     const fetchPatients = async () => {
//       try {
//         const response = await axios.get(API_BASE_URL+ '/patients');
//         setPatients(response.data);
//         setFilteredPatients(response.data);
//       } catch (error) {
//         console.error('Error fetching patients:', error);
//       }
//     };
//     fetchPatients();
//   }, []);

//   const fetchMedications = async (pmrn) => {
//     setLoadingMeds(true);
//     setErrorMeds(null);
//     try {
//       // Use the /patient/{pmrn} endpoint
//       const response = await axios.get(API_BASE_URL+`/medications/patient/${pmrn}`);

//       let current = [];
//       let history = [];

//       // Response is directly an array from /patient/{pmrn}
//       if (Array.isArray(response.data)) {
//         current = response.data;
//       } else if (response.data.current && Array.isArray(response.data.current)) {
//         current = response.data.current;
//       } else if (response.data.medications) {
//         current = response.data.medications.current || [];
//         history = response.data.medications.history || [];
//       }

//       if (response.data.history && Array.isArray(response.data.history)) {
//         history = response.data.history;
//       }

//       setCurrentMedications(current);
//       setMedicationHistory(history);
//     } catch (error) {
//       console.error('Error fetching medications:', error);
//       setCurrentMedications([]);
//       setMedicationHistory([]);
//       setErrorMeds(error.toString());
//     } finally {
//       setLoadingMeds(false);
//     }
//   };

//   const handlePatientSelect = (patient) => {
//     setSelectedPatient(patient);
//     setCurrentMedications([]);
//     setMedicationHistory([]);
//     const pmrn = patient.MRN ?? patient.id;
//     fetchMedications(pmrn);
//   };

//   useEffect(() => {
//     if (!searchTerm) {
//       setFilteredPatients(patients);
//       return;
//     }

//     const lowerSearch = searchTerm.toLowerCase();
//     const filtered = patients.filter((patient) => {
//       const patientName = `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.toLowerCase();
//       const patientId = String(patient.MRN ?? patient.id ?? '').toLowerCase();
//       const diagnosis = (patient.diagnosis ?? '').toLowerCase();
//       const gender = (patient.gender ?? '').toLowerCase();

//       return (
//         patientName.includes(lowerSearch) ||
//         patientId.includes(lowerSearch) ||
//         diagnosis.includes(lowerSearch) ||
//         gender.includes(lowerSearch)
//       );
//     });

//     setFilteredPatients(filtered);
//   }, [searchTerm, patients]);

//   const handleMedChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     if (editingMed) {
//       setEditingMed((prev) => ({
//         ...prev,
//         [name]: type === 'checkbox' ? checked : value,
//       }));
//     } else {
//       setNewMedication((prev) => ({
//         ...prev,
//         [name]: type === 'checkbox' ? checked : value,
//       }));
//     }
//   };

//   const handleAddMedication = async (e) => {
//     e.preventDefault();

//     const pmrn = selectedPatient ? (selectedPatient.MRN ?? selectedPatient.id) : newMedication.PMRN;
    
//     const payload = {
//       ...newMedication,
//       PMRN: Number(pmrn),
//       endDate: newMedication.endDate === '' ? null : newMedication.endDate,
//       indication: newMedication.indication === '' ? null : newMedication.indication,
//       brandName: newMedication.brandName === '' ? null : newMedication.brandName,
//       route: newMedication.route === '' ? null : newMedication.route,
//     };

//     setAddingMedLoading(true);
//     setAddingMedError(null);
//     try {
//       await axios.post(API_BASE_URL+'/medications', payload);

//       if (selectedPatient) {
//         fetchMedications(pmrn);
//       }

//       setShowAddMedForm(false);
//       setNewMedication({
//         PMRN: '',
//         drugName: '',
//         brandName: '',
//         strength: '',
//         form: '',
//         dosage: '',
//         frequency: '',
//         route: '',
//         startDate: '',
//         endDate: '',
//         prn: false,
//         indication: '',
//       });
//     } catch (error) {
//       setAddingMedError(error.response?.data?.detail || error.toString());
//     } finally {
//       setAddingMedLoading(false);
//     }
//   };

//   const handleUpdateMedication = async (e) => {
//     e.preventDefault();
    
//     if (!editingMed || !editingMed.id) return;

//     const payload = {
//       drugName: editingMed.drugName|| null,
//       brandName: editingMed.brandName,
//       dosage: editingMed.dosage,
//       frequency: editingMed.frequency,
//       startDate: editingMed.startDate,
//       endDate: editingMed.endDate || null,
//       prn: editingMed.prn,
//       indication: editingMed.indication || null,
//     };

//     setAddingMedLoading(true);
//     setAddingMedError(null);
//     try {
//       await axios.put(API_BASE_URL+`/medications/${editingMed.id}`, payload);
      
//       if (selectedPatient) {
//         const pmrn = selectedPatient.MRN ?? selectedPatient.id;
//         fetchMedications(pmrn);
//       }

//       setEditingMed(null);
//     } catch (error) {
//       setAddingMedError(error.response?.data?.detail || error.toString());
//     } finally {
//       setAddingMedLoading(false);
//     }
//   };

//   const handleDeleteMedication = async (medId) => {
//     if (!window.confirm('Are you sure you want to delete this medication?')) return;

//     try {
//       await axios.delete(API_BASE_URL+`/medications/${medId}`);
      
//       if (selectedPatient) {
//         const pmrn = selectedPatient.MRN ?? selectedPatient.id;
//         fetchMedications(pmrn);
//       }
//     } catch (error) {
//       alert('Error deleting medication: ' + (error.response?.data?.detail || error.toString()));
//     }
//   };

//   const startEditMedication = (med) => {
//     setEditingMed({ ...med });
//   };

//   const cancelEdit = () => {
//     setEditingMed(null);
//     setAddingMedError(null);
//   };

//   const openAddForm = () => {
//     if (selectedPatient) {
//       setNewMedication((prev) => ({
//         ...prev,
//         PMRN: selectedPatient.MRN ?? selectedPatient.id
//       }));
//     }
//     setShowAddMedForm(true);
//   };

//   const PatientTable = ({ patients, onSelectPatient }) => (
//     <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
//       <div className="p-6 border-b border-gray-200 flex justify-between items-center">
//         <h3 className="text-lg font-semibold text-gray-800">All Patients</h3>
//         <input
//           type="text"
//           placeholder="Search by Name, ID, Diagnosis, Gender"
//           value={searchTerm}
//           onChange={(e) => setSearchTerm(e.target.value)}
//           className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
//           style={{ minWidth: '250px' }}
//         />
//       </div>
//       <div className="overflow-x-auto max-h-96">
//         <table className="w-full">
//           <thead className="bg-gray-50 sticky top-0">
//             <tr>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">MRN</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Name</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Gender</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Diagnosis</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Action</th>
//             </tr>
//           </thead>
//           <tbody className="bg-white divide-y divide-gray-200">
//             {patients.map((patient) => (
//               <tr key={patient.id ?? patient.MRN} className="hover:bg-gray-50">
//                 <td className="px-6 py-4 text-sm text-gray-700">{patient.MRN ?? patient.id}</td>
//                 <td className="px-6 py-4 text-sm font-medium text-teal-600">
//                   {patient.firstName} {patient.lastName}
//                 </td>
//                 <td className="px-6 py-4 text-sm text-gray-700">{patient.gender}</td>
//                 <td className="px-6 py-4 text-sm text-gray-700">{patient.diagnosis}</td>
//                 <td className="px-6 py-4">
//                   <button
//                     onClick={() => onSelectPatient(patient)}
//                     className="text-teal-600 hover:text-teal-800 text-sm font-medium"
//                   >
//                     View Medications
//                   </button>
//                 </td>
//               </tr>
//             ))}
//             {patients.length === 0 && (
//               <tr>
//                 <td colSpan="5" className="text-center py-4 text-gray-500">
//                   No patients found
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );

//   const MedicationFormModal = ({ isEdit = false, onSubmit, onCancel }) => {
//     const formData = isEdit ? editingMed : newMedication;
    
//     return (
//     <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
//       <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
//         <h2 className="text-xl font-semibold mb-4 flex items-center space-x-2">
//           {isEdit ? <Edit2 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
//           <span>{isEdit ? 'Adjust Medication' : 'Add New Medication'}</span>
//         </h2>
//         <form onSubmit={onSubmit} className="space-y-4">
//           <div className="grid grid-cols-2 gap-4">
//             {!isEdit && (
//               <div className="col-span-2">
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Patient MRN (PMRN) *
//                 </label>
//                 <input
//                   type="number"
//                   name="PMRN"
//                   value={formData?.PMRN || ''}
//                   onChange={handleMedChange}
//                   required
//                   disabled={!!selectedPatient}
//                   className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
//                 />
//               </div>
//             )}

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">Drug Name *</label>
//               <input
//                 type="text"
//                 name="drugName"
//                 value={formData?.drugName || ''}
//                 onChange={handleMedChange}
//                 required
//                 autoFocus
//                 className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
//                 maxLength={100}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
//               <input
//                 type="text"
//                 name="brandName"
//                 value={formData?.brandName || ''}
//                 onChange={handleMedChange}
//                 className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
//                 maxLength={100}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">Strength *</label>
//               <input
//                 type="text"
//                 name="strength"
//                 value={formData?.strength || ''}
//                 onChange={handleMedChange}
//                 required
//                 className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
//                 maxLength={50}
//                 placeholder="e.g., 500mg"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">Form *</label>
//               <input
//                 type="text"
//                 name="form"
//                 value={formData?.form || ''}
//                 onChange={handleMedChange}
//                 required
//                 className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
//                 maxLength={50}
//                 placeholder="e.g., Tablet, Capsule"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">Dosage *</label>
//               <input
//                 type="text"
//                 name="dosage"
//                 value={formData?.dosage || ''}
//                 onChange={handleMedChange}
//                 required
//                 className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
//                 maxLength={100}
//                 placeholder="e.g., 1 tablet"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">Frequency *</label>
//               <input
//                 type="text"
//                 name="frequency"
//                 value={formData?.frequency || ''}
//                 onChange={handleMedChange}
//                 required
//                 className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
//                 maxLength={100}
//                 placeholder="e.g., 2x/day, 3x"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">Route</label>
//               <input
//                 type="text"
//                 name="route"
//                 value={formData?.route || ''}
//                 onChange={handleMedChange}
//                 className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
//                 maxLength={50}
//                 placeholder="e.g., Oral, IV"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
//               <input
//                 type="date"
//                 name="startDate"
//                 value={formData?.startDate || ''}
//                 onChange={handleMedChange}
//                 required
//                 className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
//               <input
//                 type="date"
//                 name="endDate"
//                 value={formData?.endDate || ''}
//                 onChange={handleMedChange}
//                 className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
//               />
//             </div>

//             <div className="col-span-2">
//               <div className="flex items-center space-x-2">
//                 <input
//                   type="checkbox"
//                   name="prn"
//                   checked={formData?.prn || false}
//                   onChange={handleMedChange}
//                   id="prnCheckbox"
//                   className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
//                 />
//                 <label htmlFor="prnCheckbox" className="text-sm text-gray-700">PRN (as needed)</label>
//               </div>
//             </div>

//             <div className="col-span-2">
//               <label className="block text-sm font-medium text-gray-700 mb-1">Indication</label>
//               <textarea
//                 name="indication"
//                 value={formData?.indication || ''}
//                 onChange={handleMedChange}
//                 rows={3}
//                 className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
//                 placeholder="Reason for prescription"
//               />
//             </div>
//           </div>

//           {addingMedError && (
//             <p className="text-red-600 text-sm">{addingMedError}</p>
//           )}

//           <div className="flex justify-end space-x-2 pt-2">
//             <button
//               type="button"
//               onClick={onCancel}
//               className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 flex items-center space-x-1"
//               disabled={addingMedLoading}
//             >
//               <X className="w-4 h-4" />
//               <span>Cancel</span>
//             </button>
//             <button
//               type="submit"
//               disabled={addingMedLoading}
//               className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-teal-400 flex items-center space-x-1"
//             >
//               <Save className="w-4 h-4" />
//               <span>{addingMedLoading ? 'Saving...' : isEdit ? 'Update' : 'Add'}</span>
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );};

//   const PatientProfileCard = ({ patient }) => {
//     if (!patient) return null;

//     return (
//       <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border border-teal-200 p-6 mb-6">
//         <div className="flex items-start justify-between">
//           <div className="flex items-start space-x-4">
//             <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center">
//               <User className="w-8 h-8 text-white" />
//             </div>
//             <div>
//               <h3 className="text-xl font-bold text-gray-800">
//                 {patient.firstName} {patient.lastName}
//               </h3>
//               <p className="text-gray-600 text-sm">MRN: #{patient.MRN ?? patient.id}</p>
//               <div className="flex items-center space-x-4 mt-3">
//                 <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
//                   <Calendar className="w-4 h-4 text-orange-500" />
//                   <span className="text-sm text-gray-700">{patient.nextVisit || 'N/A'}</span>
//                 </div>
//                 <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
//                   <Clock className="w-4 h-4 text-blue-500" />
//                   <span className="text-sm text-gray-700">{patient.lastCheckin || 'N/A'}</span>
//                 </div>
//                 <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
//                   <User className="w-4 h-4 text-teal-500" />
//                   <span className="text-sm text-gray-700">{patient.gender ?? 'N/A'}</span>
//                 </div>
//               </div>
//               <p className="mt-3 text-gray-600">{patient.diagnosis ?? 'Diagnosis not available'}</p>
//             </div>
//           </div>
//           <div>
//             <button
//               onClick={() => setSelectedPatient(null)}
//               className="text-gray-400 hover:text-red-600"
//               aria-label="Close patient profile"
//             >
//               <XCircle className="w-8 h-8" />
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   const CurrentMedicationsTable = ({ medications }) => {
//     if (!medications || medications.length === 0)
//       return <p className="text-center text-gray-500 py-8">No current medications.</p>;

//     return (
//       <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-md">
//         <table className="w-full text-sm text-left text-gray-500">
//           <thead className="text-xs uppercase bg-gray-50 text-gray-700">
//             <tr>
//               <th className="px-4 py-3">Medication</th>
//               <th className="px-4 py-3">Strength</th>
//               <th className="px-4 py-3">Form</th>
//               <th className="px-4 py-3">Dosage</th>
//               <th className="px-4 py-3">Frequency</th>
//               <th className="px-4 py-3">Route</th>
//               <th className="px-4 py-3">Start Date</th>
//               <th className="px-4 py-3">End Date</th>
//               <th className="px-4 py-3">PRN</th>
//               <th className="px-4 py-3">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {medications.map((med) => (
//               <tr key={med.id} className="bg-white border-b hover:bg-gray-50">
//                 <td className="px-4 py-3 font-medium text-teal-700">{med.drugName}</td>
//                 <td className="px-4 py-3">{med.strength}</td>
//                 <td className="px-4 py-3">{med.form}</td>
//                 <td className="px-4 py-3">{med.dosage}</td>
//                 <td className="px-4 py-3">{med.frequency}</td>
//                 <td className="px-4 py-3">{med.route || '-'}</td>
//                 <td className="px-4 py-3">{med.startDate}</td>
//                 <td className="px-4 py-3">{med.endDate || '-'}</td>
//                 <td className="px-4 py-3">{med.prn ? 'Yes' : 'No'}</td>
//                 <td className="px-4 py-3">
//                   <div className="flex space-x-2">
//                     <button
//                       onClick={() => startEditMedication(med)}
//                       className="text-blue-600 hover:text-blue-800"
//                       title="Adjust"
//                     >
//                       <Edit2 className="w-4 h-4" />
//                     </button>
//                     <button
//                       onClick={() => handleDeleteMedication(med.id)}
//                       className="text-red-600 hover:text-red-800"
//                       title="Delete"
//                     >
//                       <Trash2 className="w-4 h-4" />
//                     </button>
//                   </div>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     );
//   };

//   const MedicationsChangeHistory = ({ history }) => {
//     if (!history || history.length === 0)
//       return <p className="text-center text-gray-500 py-8">No medication history available.</p>;

//     return (
//       <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-md">
//         <table className="w-full text-sm text-left text-gray-500">
//           <thead className="text-xs uppercase bg-gray-50 text-gray-700">
//             <tr>
//               <th className="px-4 py-3">Date</th>
//               <th className="px-4 py-3">Medication</th>
//               <th className="px-4 py-3">Old Dose</th>
//               <th className="px-4 py-3">New Dose</th>
//               <th className="px-4 py-3">Old Frequency</th>
//               <th className="px-4 py-3">New Frequency</th>
//               <th className="px-4 py-3">Updated By</th>
//               <th className="px-4 py-3">Confirmed</th>
//             </tr>
//           </thead>
//           <tbody>
//             {history.map((change, idx) => (
//               <tr key={idx} className="bg-white border-b hover:bg-gray-50">
//                 <td className="px-4 py-3">{change.date || change.changeDate || '-'}</td>
//                 <td className="px-4 py-3 font-medium text-teal-700">
//                   {change.medication || change.drugName || '-'}
//                 </td>
//                 <td className="px-4 py-3">{change.oldDose || '-'}</td>
//                 <td className="px-4 py-3">{change.newDose || '-'}</td>
//                 <td className="px-4 py-3">{change.oldFrequency || '-'}</td>
//                 <td className="px-4 py-3">{change.newFrequency || '-'}</td>
//                 <td className="px-4 py-3">{change.updatedBy || '-'}</td>
//                 <td className="px-4 py-3">
//                   {change.confirm ? (
//                     <span className="text-green-600">✓ By Nurse</span>
//                   ) : change.byPatient ? (
//                     <span className="text-orange-600">✗ By Patient</span>
//                   ) : (
//                     '-'
//                   )}
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     );
//   };

//   return (
//     <>
//       <Header />
//       <main className="max-w-7xl mx-auto p-4">
//         <div className="flex justify-between items-center mb-6">
//           <h1 className="text-2xl font-bold text-gray-800">Medications Management</h1>
//           <button
//             onClick={openAddForm}
//             className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
//           >
//             <Plus className="w-5 h-5" />
//             <span>Add New Medication</span>
//           </button>
//         </div>

//         <PatientTable patients={filteredPatients} onSelectPatient={handlePatientSelect} />

//         {selectedPatient && (
//           <>
//             <PatientProfileCard patient={selectedPatient} />

//             {loadingMeds ? (
//               <p className="text-center text-gray-500 py-8">Loading medications...</p>
//             ) : errorMeds ? (
//               <p className="text-center text-red-600 py-8">Error loading medications: {errorMeds}</p>
//             ) : (
//               <>
//                 <div className="mb-4">
//                   <h3 className="text-lg font-semibold text-gray-700 mb-3">Current Medications</h3>
//                   <CurrentMedicationsTable medications={currentMedications} />
//                 </div>

//                 <div className="mt-8">
//                   <h3 className="text-lg font-semibold text-gray-700 mb-3">Medication Change History</h3>
//                   <MedicationsChangeHistory history={medicationHistory} />
//                 </div>
//               </>
//             )}
//           </>
//         )}

//         {showAddMedForm && (
//           <MedicationFormModal
//             isEdit={false}
//             onSubmit={handleAddMedication}
//             onCancel={() => {
//               setShowAddMedForm(false);
//               setAddingMedError(null);
//             }}
//           />
//         )}

//         {editingMed && (
//           <MedicationFormModal
//             isEdit={true}
//             onSubmit={handleUpdateMedication}
//             onCancel={cancelEdit}
//           />
//         )}
//       </main>
//       <Footer />
//     </>
//   );
// };

// export default MedicationsPage;


// export default MedicationsPage;


// import axios from 'axios';
// import { useState, useEffect } from 'react';
// import { User, Calendar, Clock, XCircle, Plus } from 'lucide-react';
// import Header from '../components/Header';
// import Footer from '../components/Footer';

// const MedicationsPage = () => {
//   // State
//   const [patients, setPatients] = useState([]);
//   const [filteredPatients, setFilteredPatients] = useState([]);
//   const [selectedPatient, setSelectedPatient] = useState(null);
//   const [currentMedications, setCurrentMedications] = useState([]);
//   const [medicationHistory, setMedicationHistory] = useState([]);
//   const [loadingMeds, setLoadingMeds] = useState(false);
//   const [errorMeds, setErrorMeds] = useState(null);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [showAddMedForm, setShowAddMedForm] = useState(false);
//   const [newMedication, setNewMedication] = useState({
//     PMRN: '',
//     drugName: '',
//     brandName: '',
//     strength: '',
//     form: '',
//     dosage: '',
//     frequency: '',
//     route: '',
//     startDate: '',
//     endDate: '',
//     prn: false,
//     indication: '',
//   });
//   const [addingMedLoading, setAddingMedLoading] = useState(false);
//   const [addingMedError, setAddingMedError] = useState(null);

//   // Fetch patients on mount
//   useEffect(() => {
//     const fetchPatients = async () => {
//       try {
//         const response = await axios.get('API_BASE_URL/patients');
//         setPatients(response.data);
//         setFilteredPatients(response.data);
//       } catch (error) {
//         console.error('Error fetching patients:', error);
//       }
//     };
//     fetchPatients();
//   }, []);

//   // Fetch medications when patient selected

// const fetchMedications = async (patientId) => {
//   setLoadingMeds(true);
//   setErrorMeds(null);
//   try {
//     const response = await axios.get('API_BASE_URL/medications', {
//       params: { patientId }, // <-- confirm backend expects this param
//     });

//     let current = [];
//     let history = [];

//     // Adjust parsing depending on your API response
//     if (response.data.current && Array.isArray(response.data.current)) {
//       current = response.data.current;
//     } else if (Array.isArray(response.data)) {
//       current = response.data;
//     } else if (response.data.medications) {
//       current = response.data.medications.current || [];
//       history = response.data.medications.history || [];
//     }

//     if (response.data.history && Array.isArray(response.data.history)) {
//       history = response.data.history;
//     }

//     setCurrentMedications(current);
//     setMedicationHistory(history);
//   } catch (error) {
//     console.error('Error fetching medications:', error);
//     setCurrentMedications([]);
//     setMedicationHistory([]);
//     setErrorMeds(error.toString());
//   } finally {
//     setLoadingMeds(false);
//   }
// };


//   // Handle selecting patient
//   const handlePatientSelect = (patient) => {
//     setSelectedPatient(patient);
//     setCurrentMedications([]);
//     setMedicationHistory([]);
//     const patientId = patient.id ?? patient.MRN ?? patient.mrn;
//     fetchMedications(patientId);
//   };

//   // Filter patients based on searchTerm (by patient or meds)
//   useEffect(() => {
//     if (!searchTerm) {
//       setFilteredPatients(patients);
//       return;
//     }

//     const lowerSearch = searchTerm.toLowerCase();

//     // Filter patients by name, ID, gender, diagnosis
//     const filtered = patients.filter((patient) => {
//       const patientName = `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.toLowerCase();
//       const patientId = String(patient.MRN ?? patient.id ?? '').toLowerCase();
//       const diagnosis = (patient.diagnosis ?? '').toLowerCase();
//       const gender = (patient.gender ?? '').toLowerCase();

//       // Check meds if currentMedications available
//       const medsMatch = currentMedications.some((med) => {
//         const medName = (med.drugName ?? med.name ?? '').toLowerCase();
//         const medId = String(med.id ?? '').toLowerCase();
//         return medName.includes(lowerSearch) || medId.includes(lowerSearch);
//       });

//       return (
//         patientName.includes(lowerSearch) ||
//         patientId.includes(lowerSearch) ||
//         diagnosis.includes(lowerSearch) ||
//         gender.includes(lowerSearch) ||
//         medsMatch
//       );
//     });

//     setFilteredPatients(filtered);
//   }, [searchTerm, patients, currentMedications]);

//   // Handle form input changes for new medication
//   const handleNewMedChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setNewMedication((prev) => ({
//       ...prev,
//       [name]: type === 'checkbox' ? checked : value,
//     }));
//   };

//   // Submit new medication to backend
//   const handleAddMedication = async (e) => {
//     e.preventDefault();

//     // Validate required fields
//     const requiredFields = ['PMRN', 'drugName', 'strength', 'form', 'dosage', 'frequency', 'startDate'];
//     for (const field of requiredFields) {
//       if (!newMedication[field]) {
//         alert(`Field ${field} is required.`);
//         return;
//       }
//     }

//     // Prepare payload (convert empty strings to null where appropriate)
//   const payload = {
//     ...newMedication,
//     endDate: newMedication.endDate === '' ? null : newMedication.endDate,
//     indication: newMedication.indication === '' ? null : newMedication.indication,
//     brandName: newMedication.brandName === '' ? null : newMedication.brandName,
//     route: newMedication.route === '' ? null : newMedication.route,
//   };

//     setAddingMedLoading(true);
//     setAddingMedError(null);
//     try {
//       // Send POST request to add medication
//       await axios.post('API_BASE_URL/medications', payload);

//       // After success, refetch medications for selected patient if matches PMRN
//       if (selectedPatient && (selectedPatient.MRN ?? selectedPatient.id) === Number(newMedication.PMRN)) {
//         fetchMedications(newMedication.PMRN);
//       }

//       // Close form and reset
//       setShowAddMedForm(false);
//       setNewMedication({
//         PMRN: '',
//         drugName: '',
//         brandName: '',
//         strength: '',
//         form: '',
//         dosage: '',
//         frequency: '',
//         route: '',
//         startDate: '',
//         endDate: '',
//         prn: false,
//         indication: '',
//       });
//     } catch (error) {
//       setAddingMedError(error.response?.data?.detail || error.toString());
//     } finally {
//       setAddingMedLoading(false);
//     }
//   };
    
//   // Components for UI

//   const PatientTable = ({ patients, onSelectPatient }) => (
//     <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
//       <div className="p-6 border-b border-gray-200 flex justify-between items-center">
//         <h3 className="text-lg font-semibold text-gray-800">All Patients</h3>
//         <input
//           type="text"
//           placeholder="Search by Name, ID, Diagnosis, Gender or Medication"
//           value={searchTerm}
//           onChange={(e) => setSearchTerm(e.target.value)}
//           className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-teal-500"
//           style={{ minWidth: '250px' }}
//         />
//       </div>
//       <div className="overflow-x-auto max-h-[400px]">
//         <table className="w-full">
//           <thead className="bg-gray-50">
//             <tr>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">ID / MRN</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Name</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Gender</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Diagnosis</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Action</th>
//             </tr>
//           </thead>
//           <tbody className="bg-white divide-y divide-gray-200">
//             {patients.map((patient) => (
//               <tr key={patient.id ?? patient.MRN} className="hover:bg-gray-50">
//                 <td className="px-6 py-4 text-sm text-gray-700">{patient.MRN ?? patient.id}</td>
//                 <td className="px-6 py-4 text-sm font-medium text-teal-600">
//                   {patient.firstName} {patient.lastName}
//                 </td>
//                 <td className="px-6 py-4 text-sm text-gray-700">{patient.gender}</td>
//                 <td className="px-6 py-4 text-sm text-gray-700">{patient.diagnosis}</td>
//                 <td className="px-6 py-4">
//                   <button
//                     onClick={() => onSelectPatient(patient)}
//                     className="text-teal-600 hover:text-teal-800 text-sm font-medium"
//                   >
//                     View Medications
//                   </button>
//                 </td>
//               </tr>
//             ))}
//             {patients.length === 0 && (
//               <tr>
//                 <td colSpan="5" className="text-center py-4 text-gray-500">
//                   No patients found
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );

//   // The rest components (PatientProfileCard, CurrentMedicationsTable, MedicationsChangeHistory) stay the same
//   // I will add the Add New Medication form below and integrate the Add New Medications button

//   // Add New Medication Form Modal
//   const AddMedicationForm = () => (
//     <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
//       <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6 relative">
//         <h2 className="text-xl font-semibold mb-4 flex items-center space-x-2">
//           <Plus className="w-5 h-5" /> Add New Medication
//         </h2>
//         <form onSubmit={handleAddMedication} className="space-y-4 max-h-[70vh] overflow-y-auto">
//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Patient MRN (PMRN) *
//             </label>
//             <input
//               type="number"
//               name="PMRN"
//               value={newMedication.PMRN}
//               onChange={handleNewMedChange}
//               required
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Drug Name *</label>
//             <input
//               type="text"
//               name="drugName"
//               value={newMedication.drugName}
//               onChange={handleNewMedChange}
//               required
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//               maxLength={100}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
//             <input
//               type="text"
//               name="brandName"
//               value={newMedication.brandName}
//               onChange={handleNewMedChange}
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//               maxLength={100}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Strength *</label>
//             <input
//               type="text"
//               name="strength"
//               value={newMedication.strength}
//               onChange={handleNewMedChange}
//               required
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//               maxLength={50}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Form *</label>
//             <input
//               type="text"
//               name="form"
//               value={newMedication.form}
//               onChange={handleNewMedChange}
//               required
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//               maxLength={50}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Dosage *</label>
//             <input
//               type="text"
//               name="dosage"
//               value={newMedication.dosage}
//               onChange={handleNewMedChange}
//               required
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//               maxLength={100}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Frequency *</label>
//             <input
//               type="text"
//               name="frequency"
//               value={newMedication.frequency}
//               onChange={handleNewMedChange}
//               required
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//               maxLength={100}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Route</label>
//             <input
//               type="text"
//               name="route"
//               value={newMedication.route}
//               onChange={handleNewMedChange}
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//               maxLength={50}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
//             <input
//               type="date"
//               name="startDate"
//               value={newMedication.startDate}
//               onChange={handleNewMedChange}
//               required
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
//             <input
//               type="date"
//               name="endDate"
//               value={newMedication.endDate}
//               onChange={handleNewMedChange}
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//             />
//           </div>

//           <div className="flex items-center space-x-2">
//             <input
//               type="checkbox"
//               name="prn"
//               checked={newMedication.prn}
//               onChange={handleNewMedChange}
//               id="prnCheckbox"
//               className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
//             />
//             <label htmlFor="prnCheckbox" className="text-sm text-gray-700">PRN (as needed)</label>
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Indication</label>
//             <textarea
//               name="indication"
//               value={newMedication.indication}
//               onChange={handleNewMedChange}
//               rows={3}
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//             />
//           </div>

//           {addingMedError && (
//             <p className="text-red-600 text-sm">{addingMedError}</p>
//           )}

//           <div className="flex justify-end space-x-2 pt-2">
//             <button
//               type="button"
//               onClick={() => setShowAddMedForm(false)}
//               className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
//               disabled={addingMedLoading}
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={addingMedLoading}
//               className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-teal-400"
//             >
//               {addingMedLoading ? 'Adding...' : 'Add Medication'}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );

//   // PatientProfileCard, CurrentMedicationsTable, MedicationsChangeHistory unchanged - include here

//   const PatientProfileCard = ({ patient }) => {
//     if (!patient) return null;

//     return (
//       <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border border-teal-200 p-6 mb-6">
//         <div className="flex items-start justify-between">
//           <div className="flex items-start space-x-4">
//             <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center">
//               <User className="w-8 h-8 text-white" />
//             </div>
//             <div>
//               <h3 className="text-xl font-bold text-gray-800">
//                 {patient.firstName} {patient.lastName}
//               </h3>
//               <p className="text-gray-600 text-sm">#{patient.MRN ?? patient.id}</p>
//               <div className="flex items-center space-x-4 mt-3">
//                 <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
//                   <Calendar className="w-4 h-4 text-orange-500" />
//                   <span className="text-sm text-gray-700">{patient.nextVisit || 'N/A'}</span>
//                 </div>
//                 <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
//                   <Clock className="w-4 h-4 text-blue-500" />
//                   <span className="text-sm text-gray-700">{patient.lastCheckin || 'N/A'}</span>
//                 </div>
//                 <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
//                   <User className="w-4 h-4 text-teal-500" />
//                   <span className="text-sm text-gray-700">{patient.gender ?? 'N/A'}</span>
//                 </div>
//               </div>
//               <p className="mt-3 text-gray-600">{patient.diagnosis ?? 'Diagnosis not available'}</p>
//             </div>
//           </div>
//           <div>
//             <button
//               onClick={() => setSelectedPatient(null)}
//               className="text-gray-400 hover:text-red-600"
//               aria-label="Close patient profile"
//             >
//               <XCircle className="w-8 h-8" />
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   const CurrentMedicationsTable = ({ medications }) => {
//     if (!medications || medications.length === 0)
//       return <p className="text-center text-gray-500">No current medications.</p>;

//     return (
//       <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-md">
//         <table className="w-full table-fixed text-sm text-left text-gray-500">
//           <thead className="text-xs uppercase bg-gray-50 text-gray-700">
//             <tr>
//               <th className="px-4 py-3">Medication</th>
//               <th className="px-4 py-3">Strength</th>
//               <th className="px-4 py-3">Form</th>
//               <th className="px-4 py-3">Dosage</th>
//               <th className="px-4 py-3">Frequency</th>
//               <th className="px-4 py-3">Route</th>
//               <th className="px-4 py-3">Start Date</th>
//               <th className="px-4 py-3">End Date</th>
//               <th className="px-4 py-3">PRN</th>
//               <th className="px-4 py-3">Indication</th>
//             </tr>
//           </thead>
//           <tbody>
//             {medications.map((med) => (
//               <tr key={med.id} className="bg-white border-b hover:bg-gray-50">
//                 <td className="px-4 py-3 font-medium text-teal-700">{med.drugName}</td>
//                 <td className="px-4 py-3">{med.strength}</td>
//                 <td className="px-4 py-3">{med.form}</td>
//                 <td className="px-4 py-3">{med.dosage}</td>
//                 <td className="px-4 py-3">{med.frequency}</td>
//                 <td className="px-4 py-3">{med.route}</td>
//                 <td className="px-4 py-3">{med.startDate}</td>
//                 <td className="px-4 py-3">{med.endDate || '-'}</td>
//                 <td className="px-4 py-3">{med.prn ? 'Yes' : 'No'}</td>
//                 <td className="px-4 py-3">{med.indication || '-'}</td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     );
//   };

//   const MedicationsChangeHistory = ({ history }) => {
//     if (!history || history.length === 0)
//       return <p className="text-center text-gray-500">No medication history available.</p>;

//     return (
//       <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-md mt-6">
//         <table className="w-full table-fixed text-sm text-left text-gray-500">
//           <thead className="text-xs uppercase bg-gray-50 text-gray-700">
//             <tr>
//               <th className="px-4 py-3">Medication</th>
//               <th className="px-4 py-3">Change Date</th>
//               <th className="px-4 py-3">Change Description</th>
//             </tr>
//           </thead>
//           <tbody>
//             {history.map((change, idx) => (
//               <tr key={idx} className="bg-white border-b hover:bg-gray-50">
//                 <td className="px-4 py-3 font-medium text-teal-700">{change.drugName || change.medication}</td>
//                 <td className="px-4 py-3">{change.changeDate || change.date || '-'}</td>
//                 <td className="px-4 py-3">{change.description || change.changeDescription || '-'}</td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     );
//   };

//   return (
//     <>
//       <Header />
//       <main className="max-w-7xl mx-auto p-4">
//             <div className="flex flex-col items-end mb-6">
//                 <button
//                 onClick={() => setShowAddMedForm(true)}
//                 className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-md"
//               >
//                 Add New Medication
//                 </button>
//             </div>
//         <PatientTable patients={filteredPatients} onSelectPatient={handlePatientSelect} />

//         {selectedPatient && (
//           <>
//             <div className="flex justify-between items-center mb-4">
//               <PatientProfileCard patient={selectedPatient} />
              
//             </div>

//             {loadingMeds ? (
//               <p className="text-center text-gray-500">Loading medications...</p>
//             ) : errorMeds ? (
//               <p className="text-center text-red-600">Error loading medications: {errorMeds}</p>
//             ) : (
//               <>
//                 <h3 className="text-lg font-semibold text-gray-700 mb-2">Current Medications</h3>
//                 <CurrentMedicationsTable medications={currentMedications} />

//                 <h3 className="text-lg font-semibold text-gray-700 mt-6 mb-2">Medication Change History</h3>
//                 <MedicationsChangeHistory history={medicationHistory} />
//               </>
//             )}
//           </>
//         )}

//         {showAddMedForm && <AddMedicationForm />}
//       </main>
//       <Footer />
//     </>
//   );
// };

// export default MedicationsPage;


// ------------------------------------------------------------------------------------------------------------------------ 

// import axios from 'axios';
// import { useState, useEffect } from 'react';
// import { User, Calendar, Clock, XCircle, Plus } from 'lucide-react';
// import Header from '../components/Header';
// import Footer from '../components/Footer';

// const MedicationsPage = () => {
//   // State
//   const [patients, setPatients] = useState([]);
//   const [filteredPatients, setFilteredPatients] = useState([]);
//   const [selectedPatient, setSelectedPatient] = useState(null);
//   const [currentMedications, setCurrentMedications] = useState([]);
//   const [medicationHistory, setMedicationHistory] = useState([]);
//   const [loadingMeds, setLoadingMeds] = useState(false);
//   const [errorMeds, setErrorMeds] = useState(null);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [showAddMedForm, setShowAddMedForm] = useState(false);
//   const [newMedication, setNewMedication] = useState({
//     PMRN: '',
//     drugName: '',
//     brandName: '',
//     strength: '',
//     form: '',
//     dosage: '',
//     frequency: '',
//     route: '',
//     startDate: '',
//     endDate: '',
//     prn: false,
//     indication: '',
//   });
//   const [addingMedLoading, setAddingMedLoading] = useState(false);
//   const [addingMedError, setAddingMedError] = useState(null);

//   // Fetch patients on mount
//   useEffect(() => {
//     const fetchPatients = async () => {
//       try {
//         const response = await axios.get('API_BASE_URL/patients');
//         setPatients(response.data);
//         setFilteredPatients(response.data);
//       } catch (error) {
//         console.error('Error fetching patients:', error);
//       }
//     };
//     fetchPatients();
//   }, []);

//   // Fetch medications when patient selected
//   const fetchMedications = async (patientId) => {
//     setLoadingMeds(true);
//     setErrorMeds(null);
//     try {
//       const response = await axios.get('API_BASE_URL/medications', {
//         params: { patientId },
//       });

//       let current = [];
//       let history = [];

//       if (response.data.current && Array.isArray(response.data.current)) {
//         current = response.data.current;
//       } else if (Array.isArray(response.data)) {
//         current = response.data;
//       } else if (response.data.medications) {
//         current = response.data.medications.current || [];
//         history = response.data.medications.history || [];
//       }

//       if (response.data.history && Array.isArray(response.data.history)) {
//         history = response.data.history;
//       }

//       setCurrentMedications(current);
//       setMedicationHistory(history);
//     } catch (error) {
//       console.error('Error fetching medications:', error);
//       setCurrentMedications([]);
//       setMedicationHistory([]);
//       setErrorMeds(error.toString());
//     } finally {
//       setLoadingMeds(false);
//     }
//   };

//   // Handle selecting patient
//   const handlePatientSelect = (patient) => {
//     setSelectedPatient(patient);
//     setCurrentMedications([]);
//     setMedicationHistory([]);
//     const patientId = patient.id ?? patient.MRN ?? patient.mrn;
//     fetchMedications(patientId);
//   };

//   // Filter patients based on searchTerm (by patient or meds)
//   useEffect(() => {
//     if (!searchTerm) {
//       setFilteredPatients(patients);
//       return;
//     }

//     const lowerSearch = searchTerm.toLowerCase();

//     // Filter patients by name, ID, gender, diagnosis
//     const filtered = patients.filter((patient) => {
//       const patientName = `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.toLowerCase();
//       const patientId = String(patient.MRN ?? patient.id ?? '').toLowerCase();
//       const diagnosis = (patient.diagnosis ?? '').toLowerCase();
//       const gender = (patient.gender ?? '').toLowerCase();

//       // Check meds if currentMedications available
//       const medsMatch = currentMedications.some((med) => {
//         const medName = (med.drugName ?? med.name ?? '').toLowerCase();
//         const medId = String(med.id ?? '').toLowerCase();
//         return medName.includes(lowerSearch) || medId.includes(lowerSearch);
//       });

//       return (
//         patientName.includes(lowerSearch) ||
//         patientId.includes(lowerSearch) ||
//         diagnosis.includes(lowerSearch) ||
//         gender.includes(lowerSearch) ||
//         medsMatch
//       );
//     });

//     setFilteredPatients(filtered);
//   }, [searchTerm, patients, currentMedications]);

//   // Handle form input changes for new medication
//   const handleNewMedChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setNewMedication((prev) => ({
//       ...prev,
//       [name]: type === 'checkbox' ? checked : value,
//     }));
//   };

//   // Submit new medication to backend
//   const handleAddMedication = async (e) => {
//     e.preventDefault();

//     // Validate required fields
//     const requiredFields = ['PMRN', 'drugName', 'strength', 'form', 'dosage', 'frequency', 'startDate'];
//     for (const field of requiredFields) {
//       if (!newMedication[field]) {
//         alert(`Field ${field} is required.`);
//         return;
//       }
//     }

//     // Prepare payload (convert empty strings to null where appropriate)
//   const payload = {
//     ...newMedication,
//     endDate: newMedication.endDate === '' ? null : newMedication.endDate,
//     indication: newMedication.indication === '' ? null : newMedication.indication,
//     brandName: newMedication.brandName === '' ? null : newMedication.brandName,
//     route: newMedication.route === '' ? null : newMedication.route,
//   };

//     setAddingMedLoading(true);
//     setAddingMedError(null);
//     try {
//       // Send POST request to add medication
//       await axios.post('API_BASE_URL/medications', payload);

//       // After success, refetch medications for selected patient if matches PMRN
//       if (selectedPatient && (selectedPatient.MRN ?? selectedPatient.id) === Number(newMedication.PMRN)) {
//         fetchMedications(newMedication.PMRN);
//       }

//       // Close form and reset
//       setShowAddMedForm(false);
//       setNewMedication({
//         PMRN: '',
//         drugName: '',
//         brandName: '',
//         strength: '',
//         form: '',
//         dosage: '',
//         frequency: '',
//         route: '',
//         startDate: '',
//         endDate: '',
//         prn: false,
//         indication: '',
//       });
//     } catch (error) {
//       setAddingMedError(error.response?.data?.detail || error.toString());
//     } finally {
//       setAddingMedLoading(false);
//     }
//   };
    
//   // Components for UI

//   const PatientTable = ({ patients, onSelectPatient }) => (
//     <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
//       <div className="p-6 border-b border-gray-200 flex justify-between items-center">
//         <h3 className="text-lg font-semibold text-gray-800">All Patients</h3>
//         <input
//           type="text"
//           placeholder="Search by Name, ID, Diagnosis, Gender or Medication"
//           value={searchTerm}
//           onChange={(e) => setSearchTerm(e.target.value)}
//           className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-teal-500"
//           style={{ minWidth: '250px' }}
//         />
//       </div>
//       <div className="overflow-x-auto max-h-[400px]">
//         <table className="w-full">
//           <thead className="bg-gray-50">
//             <tr>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">ID / MRN</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Name</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Gender</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Diagnosis</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Action</th>
//             </tr>
//           </thead>
//           <tbody className="bg-white divide-y divide-gray-200">
//             {patients.map((patient) => (
//               <tr key={patient.id ?? patient.MRN} className="hover:bg-gray-50">
//                 <td className="px-6 py-4 text-sm text-gray-700">{patient.MRN ?? patient.id}</td>
//                 <td className="px-6 py-4 text-sm font-medium text-teal-600">
//                   {patient.firstName} {patient.lastName}
//                 </td>
//                 <td className="px-6 py-4 text-sm text-gray-700">{patient.gender}</td>
//                 <td className="px-6 py-4 text-sm text-gray-700">{patient.diagnosis}</td>
//                 <td className="px-6 py-4">
//                   <button
//                     onClick={() => onSelectPatient(patient)}
//                     className="text-teal-600 hover:text-teal-800 text-sm font-medium"
//                   >
//                     View Medications
//                   </button>
//                 </td>
//               </tr>
//             ))}
//             {patients.length === 0 && (
//               <tr>
//                 <td colSpan="5" className="text-center py-4 text-gray-500">
//                   No patients found
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );

//   // The rest components (PatientProfileCard, CurrentMedicationsTable, MedicationsChangeHistory) stay the same
//   // I will add the Add New Medication form below and integrate the Add New Medications button

//   // Add New Medication Form Modal
//   const AddMedicationForm = () => (
//     <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
//       <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6 relative">
//         <h2 className="text-xl font-semibold mb-4 flex items-center space-x-2">
//           <Plus className="w-5 h-5" /> Add New Medication
//         </h2>
//         <form onSubmit={handleAddMedication} className="space-y-4 max-h-[70vh] overflow-y-auto">
//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Patient MRN (PMRN) *
//             </label>
//             <input
//               type="number"
//               name="PMRN"
//               value={newMedication.PMRN}
//               onChange={handleNewMedChange}
//               required
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Drug Name *</label>
//             <input
//               type="text"
//               name="drugName"
//               value={newMedication.drugName}
//               onChange={handleNewMedChange}
//               required
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//               maxLength={100}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
//             <input
//               type="text"
//               name="brandName"
//               value={newMedication.brandName}
//               onChange={handleNewMedChange}
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//               maxLength={100}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Strength *</label>
//             <input
//               type="text"
//               name="strength"
//               value={newMedication.strength}
//               onChange={handleNewMedChange}
//               required
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//               maxLength={50}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Form *</label>
//             <input
//               type="text"
//               name="form"
//               value={newMedication.form}
//               onChange={handleNewMedChange}
//               required
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//               maxLength={50}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Dosage *</label>
//             <input
//               type="text"
//               name="dosage"
//               value={newMedication.dosage}
//               onChange={handleNewMedChange}
//               required
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//               maxLength={100}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Frequency *</label>
//             <input
//               type="text"
//               name="frequency"
//               value={newMedication.frequency}
//               onChange={handleNewMedChange}
//               required
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//               maxLength={100}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Route</label>
//             <input
//               type="text"
//               name="route"
//               value={newMedication.route}
//               onChange={handleNewMedChange}
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//               maxLength={50}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
//             <input
//               type="date"
//               name="startDate"
//               value={newMedication.startDate}
//               onChange={handleNewMedChange}
//               required
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
//             <input
//               type="date"
//               name="endDate"
//               value={newMedication.endDate}
//               onChange={handleNewMedChange}
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//             />
//           </div>

//           <div className="flex items-center space-x-2">
//             <input
//               type="checkbox"
//               name="prn"
//               checked={newMedication.prn}
//               onChange={handleNewMedChange}
//               id="prnCheckbox"
//               className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
//             />
//             <label htmlFor="prnCheckbox" className="text-sm text-gray-700">PRN (as needed)</label>
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Indication</label>
//             <textarea
//               name="indication"
//               value={newMedication.indication}
//               onChange={handleNewMedChange}
//               rows={3}
//               className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-teal-500"
//             />
//           </div>

//           {addingMedError && (
//             <p className="text-red-600 text-sm">{addingMedError}</p>
//           )}

//           <div className="flex justify-end space-x-2 pt-2">
//             <button
//               type="button"
//               onClick={() => setShowAddMedForm(false)}
//               className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
//               disabled={addingMedLoading}
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={addingMedLoading}
//               className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-teal-400"
//             >
//               {addingMedLoading ? 'Adding...' : 'Add Medication'}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );

//   // PatientProfileCard, CurrentMedicationsTable, MedicationsChangeHistory unchanged - include here

//   const PatientProfileCard = ({ patient }) => {
//     if (!patient) return null;

//     return (
//       <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border border-teal-200 p-6 mb-6">
//         <div className="flex items-start justify-between">
//           <div className="flex items-start space-x-4">
//             <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center">
//               <User className="w-8 h-8 text-white" />
//             </div>
//             <div>
//               <h3 className="text-xl font-bold text-gray-800">
//                 {patient.firstName} {patient.lastName}
//               </h3>
//               <p className="text-gray-600 text-sm">#{patient.MRN ?? patient.id}</p>
//               <div className="flex items-center space-x-4 mt-3">
//                 <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
//                   <Calendar className="w-4 h-4 text-orange-500" />
//                   <span className="text-sm text-gray-700">{patient.nextVisit || 'N/A'}</span>
//                 </div>
//                 <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
//                   <Clock className="w-4 h-4 text-blue-500" />
//                   <span className="text-sm text-gray-700">{patient.lastCheckin || 'N/A'}</span>
//                 </div>
//                 <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
//                   <User className="w-4 h-4 text-teal-500" />
//                   <span className="text-sm text-gray-700">{patient.gender ?? 'N/A'}</span>
//                 </div>
//               </div>
//               <p className="mt-3 text-gray-600">{patient.diagnosis ?? 'Diagnosis not available'}</p>
//             </div>
//           </div>
//           <div>
//             <button
//               onClick={() => setSelectedPatient(null)}
//               className="text-gray-400 hover:text-red-600"
//               aria-label="Close patient profile"
//             >
//               <XCircle className="w-8 h-8" />
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   const CurrentMedicationsTable = ({ medications }) => {
//     if (!medications || medications.length === 0)
//       return <p className="text-center text-gray-500">No current medications.</p>;

//     return (
//       <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-md">
//         <table className="w-full table-fixed text-sm text-left text-gray-500">
//           <thead className="text-xs uppercase bg-gray-50 text-gray-700">
//             <tr>
//               <th className="px-4 py-3">Medication</th>
//               <th className="px-4 py-3">Strength</th>
//               <th className="px-4 py-3">Form</th>
//               <th className="px-4 py-3">Dosage</th>
//               <th className="px-4 py-3">Frequency</th>
//               <th className="px-4 py-3">Route</th>
//               <th className="px-4 py-3">Start Date</th>
//               <th className="px-4 py-3">End Date</th>
//               <th className="px-4 py-3">PRN</th>
//               <th className="px-4 py-3">Indication</th>
//             </tr>
//           </thead>
//           <tbody>
//             {medications.map((med) => (
//               <tr key={med.id} className="bg-white border-b hover:bg-gray-50">
//                 <td className="px-4 py-3 font-medium text-teal-700">{med.drugName}</td>
//                 <td className="px-4 py-3">{med.strength}</td>
//                 <td className="px-4 py-3">{med.form}</td>
//                 <td className="px-4 py-3">{med.dosage}</td>
//                 <td className="px-4 py-3">{med.frequency}</td>
//                 <td className="px-4 py-3">{med.route}</td>
//                 <td className="px-4 py-3">{med.startDate}</td>
//                 <td className="px-4 py-3">{med.endDate || '-'}</td>
//                 <td className="px-4 py-3">{med.prn ? 'Yes' : 'No'}</td>
//                 <td className="px-4 py-3">{med.indication || '-'}</td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     );
//   };

//   const MedicationsChangeHistory = ({ history }) => {
//     if (!history || history.length === 0)
//       return <p className="text-center text-gray-500">No medication history available.</p>;

//     return (
//       <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-md mt-6">
//         <table className="w-full table-fixed text-sm text-left text-gray-500">
//           <thead className="text-xs uppercase bg-gray-50 text-gray-700">
//             <tr>
//               <th className="px-4 py-3">Medication</th>
//               <th className="px-4 py-3">Change Date</th>
//               <th className="px-4 py-3">Change Description</th>
//             </tr>
//           </thead>
//           <tbody>
//             {history.map((change, idx) => (
//               <tr key={idx} className="bg-white border-b hover:bg-gray-50">
//                 <td className="px-4 py-3 font-medium text-teal-700">{change.drugName || change.medication}</td>
//                 <td className="px-4 py-3">{change.changeDate || change.date || '-'}</td>
//                 <td className="px-4 py-3">{change.description || change.changeDescription || '-'}</td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     );
//   };

//   return (
//     <>
//       <Header />
//       <main className="max-w-7xl mx-auto p-4">
//         <PatientTable patients={filteredPatients} onSelectPatient={handlePatientSelect} />

//         {selectedPatient && (
//           <>
//             <div className="flex justify-between items-center mb-4">
//               <PatientProfileCard patient={selectedPatient} />
//               <button
//                 onClick={() => setShowAddMedForm(true)}
//                 className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-md"
//               >
//                 Add New Medication
//               </button>
//             </div>

//             {loadingMeds ? (
//               <p className="text-center text-gray-500">Loading medications...</p>
//             ) : errorMeds ? (
//               <p className="text-center text-red-600">Error loading medications: {errorMeds}</p>
//             ) : (
//               <>
//                 <h3 className="text-lg font-semibold text-gray-700 mb-2">Current Medications</h3>
//                 <CurrentMedicationsTable medications={currentMedications} />

//                 <h3 className="text-lg font-semibold text-gray-700 mt-6 mb-2">Medication Change History</h3>
//                 <MedicationsChangeHistory history={medicationHistory} />
//               </>
//             )}
//           </>
//         )}

//         {showAddMedForm && <AddMedicationForm />}
//       </main>
//       <Footer />
//     </>
//   );
// };

// export default MedicationsPage;

// ---------------------------------------------------------------------------------------------------------------------------------
// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import { User, Calendar, Clock, XCircle } from 'lucide-react';
// import Header from '../components/Header';
// import Footer from '../components/Footer';

// const MedicationsPage = () => {
//   const [patients, setPatients] = useState([]);
//   const [selectedPatient, setSelectedPatient] = useState(null);
//   const [allMedications, setAllMedications] = useState([]);
//   const [loadingMeds, setLoadingMeds] = useState(false);
//   const [errorMeds, setErrorMeds] = useState(null);
//   const [medicationHistory, setMedicationHistory] = useState([]); // optional

//   // Fetch all patients on mount
//   useEffect(() => {
//     const fetchPatients = async () => {
//       try {
//         const response = await axios.get('API_BASE_URL/patients');
//         setPatients(response.data);
//       } catch (error) {
//         console.error('Error fetching patients:', error);
//       }
//     };
//     fetchPatients();
//   }, []);

//   // Fetch ALL medications once on mount
//   useEffect(() => {
//     const fetchAllMedications = async () => {
//       setLoadingMeds(true);
//       setErrorMeds(null);
//       try {
//         const response = await axios.get('API_BASE_URL/medications');
//         // Assuming response.data is an array of all meds
//         setAllMedications(response.data);
//       } catch (error) {
//         console.error('Error fetching medications:', error);
//         setErrorMeds('Failed to load medications');
//       } finally {
//         setLoadingMeds(false);
//       }
//     };
//     fetchAllMedications();
//   }, []);

//   // Filter meds for the selected patient by PMRN matching patient's MRN
//   const currentMedications = selectedPatient
//     ? allMedications.filter(med => med.PMRN === selectedPatient.MRN)
//     : [];

//   const handlePatientSelect = (patient) => {
//     setSelectedPatient(patient);
//     // Clear meds history if needed
//     setMedicationHistory([]);
//   };

//   const PatientTable = ({ patients, onSelectPatient }) => (
//     <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
//       <div className="p-6 border-b border-gray-200">
//         <h3 className="text-lg font-semibold text-gray-800">All Patients</h3>
//       </div>
//       <div className="overflow-x-auto">
//         <table className="w-full">
//           <thead className="bg-gray-50">
//             <tr>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">ID / MRN</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Name</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Gender</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Diagnosis</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Action</th>
//             </tr>
//           </thead>
//           <tbody className="bg-white divide-y divide-gray-200">
//             {patients.map((patient) => (
//               <tr key={patient.id ?? patient.MRN} className="hover:bg-gray-50">
//                 <td className="px-6 py-4 text-sm text-gray-700">{patient.MRN ?? patient.id}</td>
//                 <td className="px-6 py-4 text-sm font-medium text-teal-600">
//                   {patient.firstName} {patient.lastName}
//                 </td>
//                 <td className="px-6 py-4 text-sm text-gray-700">{patient.gender}</td>
//                 <td className="px-6 py-4 text-sm text-gray-700">{patient.diagnosis}</td>
//                 <td className="px-6 py-4">
//                   <button
//                     onClick={() => onSelectPatient(patient)}
//                     className="text-teal-600 hover:text-teal-800 text-sm font-medium"
//                   >
//                     View Medications
//                   </button>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );

//   const PatientProfileCard = ({ patient }) => {
//     if (!patient) return null;

//     return (
//       <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border border-teal-200 p-6 mb-6">
//         <div className="flex items-start justify-between">
//           <div className="flex items-start space-x-4">
//             <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center">
//               <User className="w-8 h-8 text-white" />
//             </div>
//             <div>
//               <h3 className="text-xl font-bold text-gray-800">
//                 {patient.firstName} {patient.lastName}
//               </h3>
//               <p className="text-gray-600 text-sm">#{patient.MRN ?? patient.id}</p>
//               <div className="flex items-center space-x-4 mt-3">
//                 <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
//                   <Calendar className="w-4 h-4 text-orange-500" />
//                   <span className="text-sm text-gray-700">{patient.nextVisit || 'N/A'}</span>
//                 </div>
//                 <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
//                   <Clock className="w-4 h-4 text-blue-500" />
//                   <span className="text-sm text-gray-700">{patient.lastCheckin || 'N/A'}</span>
//                 </div>
//                 <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full">
//                   <span className="text-sm text-gray-700">{patient.vitals || 'No vitals'}</span>
//                 </div>
//               </div>
//             </div>
//           </div>
//           <button
//             onClick={() => {
//               setSelectedPatient(null);
//               setMedicationHistory([]);
//             }}
//             className="bg-white text-teal-600 border border-teal-600 px-4 py-2 rounded-lg hover:bg-teal-50 text-sm"
//           >
//             Close Patient View
//           </button>
//         </div>

//         <div className="mt-4 bg-white rounded-lg p-4">
//           <div className="flex items-center space-x-2">
//             <XCircle className="w-5 h-5 text-red-500" />
//             <span className="font-semibold text-gray-800">{currentMedications.length}</span>
//             <span className="text-gray-600 text-sm">Patient Medications</span>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   const CurrentMedicationsTable = ({ medications }) => (
//     <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
//       <div className="p-6 border-b border-gray-200">
//         <h3 className="text-lg font-semibold text-gray-800">Current Medications</h3>
//       </div>
//       <div className="overflow-x-auto">
//         <table className="w-full">
//           <thead className="bg-gray-50">
//             <tr>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Medication</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Dose</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Frequency</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Start Date</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Assigned To</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Action</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Recommendation</th>
//             </tr>
//           </thead>
//           <tbody className="bg-white divide-y divide-gray-200">
//             {medications.length > 0 ? (
//               medications.map((med, index) => (
//                 <tr key={index} className="hover:bg-gray-50">
//                   <td className="px-6 py-4 text-sm font-medium text-teal-600">
//                     {med.drugName ?? med.name ?? '(no name)'}
//                   </td>
//                   <td className="px-6 py-4 text-sm text-gray-700">
//                     {med.dosage ?? med.dose ?? '—'}
//                   </td>
//                   <td className="px-6 py-4 text-sm text-gray-700">
//                     {med.frequency ?? '—'}
//                   </td>
//                   <td className="px-6 py-4 text-sm text-gray-700">
//                     {med.startDate ?? med.start_date ?? '—'}
//                   </td>
//                   <td className="px-6 py-4 text-sm text-gray-700">
//                     {med.assignedTo ?? med.assigned_to ?? '—'}
//                   </td>
//                   <td className="px-6 py-4">
//                     <span className="bg-teal-600 text-white px-3 py-1 rounded-full text-xs font-medium">
//                       {med.action ?? '—'}
//                     </span>
//                   </td>
//                   <td className="px-6 py-4 text-sm text-gray-600">
//                     {med.recommendation ?? '—'}
//                   </td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
//                   No medications found for this patient.
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );

//   const MedicationsChangeHistory = ({ history }) => (
//     <div className="bg-white rounded-lg shadow-sm border border-gray-200">
//       <div className="p-6 border-b border-gray-200">
//         <h3 className="text-lg font-semibold text-gray-800">Medication Change History</h3>
//       </div>
//       <div className="overflow-x-auto">
//         <table className="w-full">
//           <thead className="bg-gray-50">
//             <tr>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Medication</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Date</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Old Dose</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">New Dose</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Old Frequency</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">New Frequency</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Updated By</th>
//             </tr>
//           </thead>
//           <tbody className="bg-white divide-y divide-gray-200">
//             {history.length > 0 ? (
//               history.map((item, index) => (
//                 <tr key={index} className="hover:bg-gray-50">
//                   <td className="px-6 py-4 text-sm font-medium text-teal-600">
//                     {item.medication ?? item.name ?? '(no name)'}
//                   </td>
//                   <td className="px-6 py-4 text-sm text-gray-700">{item.date ?? '—'}</td>
//                   <td className="px-6 py-4 text-sm text-gray-700">{item.oldDose ?? item.old_dose ?? '—'}</td>
//                   <td className="px-6 py-4 text-sm text-gray-700">{item.newDose ?? item.new_dose ?? '—'}</td>
//                   <td className="px-6 py-4 text-sm text-gray-700">{item.oldFrequency ?? item.old_freq ?? '—'}</td>
//                   <td className="px-6 py-4 text-sm text-gray-700">{item.newFrequency ?? item.new_freq ?? '—'}</td>
//                   <td className="px-6 py-4 text-sm text-gray-700">{item.updatedBy ?? item.updated_by ?? '—'}</td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
//                   No medication history available.
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );

//   return (
//     <div className="min-h-screen flex flex-col bg-gray-50">
//       <Header />

//       <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
//         <h1 className="text-3xl font-bold text-gray-800 mb-6">Medications</h1>

//         <PatientTable patients={patients} onSelectPatient={handlePatientSelect} />

//         {selectedPatient && (
//           <>
//             <PatientProfileCard patient={selectedPatient} />

//             {loadingMeds && <p>Loading medications…</p>}
//             {errorMeds && <p className="text-red-600">Error: {errorMeds}</p>}

//             <CurrentMedicationsTable medications={currentMedications} />
//             <MedicationsChangeHistory history={medicationHistory} />
//           </>
//         )}
//       </main>

//       <Footer />
//     </div>
//   );
// };

// export default MedicationsPage;

// ------------------------------------------------------------------------------------------------------------




// import React, { useState, useEffect } from 'react';
// import axios from 'axios';

// import Header from '../components/Header';
// import Footer from '../components/Footer';
// import FilterSection from '../components/FilterSection';
// import MedsCard from '../components/MedsCard';



// const medicationsFilters = [
//     { name: 'name', label: 'Patient Name', type: 'text', placeholder: 'Enter patient name' },
//     { name: 'ID', label: 'Patient ID', type: 'text', placeholder: 'Enter patient ID' },
//     { name: 'diagnosis', label: 'Diagnosis', type: 'select', options: ['Diabetes', 'Hypertension', 'Heart Failure', 'Thyroid'] },
//     { name: 'clinician', label: 'Assigned Clinician', type: 'select', options: ['Dr. A', 'Dr. B', 'Dr. C'] },
//     { name: 'age', label: 'Age', type: 'range', min: 1, max: 100, defaultValue: 0 },
//     { name: 'gender', label: 'Gender', type: 'radio', options: ['Male', 'Female'] },
// ];


// const MedicationsPage = () => {
//     const [medications, setMedications] = useState([]);
//     const [filtered, setFiltered] = useState([]);
//     const [activeFilters, setActiveFilters] = useState({});

//     useEffect(() => {
//         const fetchMedications = async () => {
//             try {
//                 const response = await axios.get('API_BASE_URL/medications/'); // Replace with your FastAPI endpoint
//                 setMedications(response.data);
//                 setFiltered(response.data);
//             } catch (error) {
//                 console.error('Error fetching medications:', error);
//             }
//         };

//         fetchMedications();
//     }, []);

//     const handleFilter = (filters) => {
//         setActiveFilters(filters);

//         const filteredList = medications.filter((p) => {
//             return (
//                 (!filters.name || p.name.toLowerCase().includes(filters.name.toLowerCase())) &&
//                 (!filters.diagnosis || p.diagnosis === filters.diagnosis) &&
//                 (!filters.clinician || p.clinician === filters.clinician) &&
//                 (!filters.age || p.age >= filters.age) &&
//                 (!filters.gender || p.gender === filters.gender)
//             );
//         });

//         setFiltered(filteredList);
//     };

//     const removeFilter = (key) => {
//         const updatedFilters = { ...activeFilters };
//         delete updatedFilters[key];
//         setActiveFilters(updatedFilters);
//         handleFilter(updatedFilters);
//     };


//     return (

//         <div className="min-h-screen flex flex-col bg-gray-50">
//             <Header />
//             <main className="flex-grow w-full max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-6">

//                 {/* Flex container for headings and button */}
//                 <div className="flex justify-between items-center mb-6">

//                     <h2 className="text-2xl font-bold text-gray-800">Medications</h2>

//                     <button
//                         // onClick={() => setShowModal(true)}
//                         className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700"
//                     >
//                         Add Medications
//                     </button>
//                 </div>

//                 {/* Filter Section */}
//                 <FilterSection fields={medicationsFilters} onFilter={handleFilter} />

//                 {/* Active Filters Tags */}
//                 {Object.entries(activeFilters).filter(([key, value]) => value).length > 0 && (
//                     <div className="flex flex-wrap gap-2 mb-4">
//                         {Object.entries(activeFilters).map(([key, value]) => (
//                             value && (
//                                 <div key={key} className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full flex items-center text-sm">
//                                     <span>{key}: {value}</span>
//                                     <button
//                                         className="ml-2 text-teal-800 hover:text-teal-900"
//                                         onClick={() => removeFilter(key)}
//                                     >
//                                         ×
//                                     </button>
//                                 </div>
//                             )
//                         ))}
//                     </div>
//                 )}

//                 {/* Filtered Results */}
//                 <div className="bg-white shadow p-6 rounded-md my-4">
//                     {filtered.length === 0 ? (
//                         <p className="text-gray-500">No matching medications found.</p>
//                     ) : (
//                         filtered.map((medications, index) => (
//                             <MedsCard key={index} medication={medications} />
//                         ))
//                     )}
//                 </div>
//             </main>
//             <Footer />
//         </div>
//     );
// };
// export default MedicationsPage;
